<script setup>
/**
 * The queue table and its three non-list states.
 *
 * Loading renders skeleton rows in the real column layout rather than a
 * spinner, so the page does not reflow when the rows arrive. The two empty
 * states are different screens on purpose: an account with no tickets is
 * taught how they appear, a filter set with no matches is offered its way
 * back. An error says the fetch failed and offers a retry — never an empty
 * table, which would read as "no tickets".
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { BaseTable } from 'dashboard/components-next/table';
import Button from 'dashboard/components-next/button/Button.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import CaseListRow from './CaseListRow.vue';

defineProps({
  cases: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  // Truthy when the last fetch failed; the message is the screen's to own.
  hasError: {
    type: Boolean,
    default: false,
  },
  // Distinguishes "no tickets yet" from "nothing matches these filters".
  isFiltered: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select', 'retry', 'clearFilters']);

const SKELETON_ROWS = 6;

const { t } = useI18n();

const headers = computed(() => [
  t('SUPPORT_CASES.LIST.TABLE.CASE'),
  t('SUPPORT_CASES.LIST.TABLE.TYPE'),
  t('SUPPORT_CASES.LIST.TABLE.STAGE'),
  t('SUPPORT_CASES.LIST.TABLE.OWNER'),
  t('SUPPORT_CASES.LIST.TABLE.VISIBILITY'),
  t('SUPPORT_CASES.LIST.TABLE.LAST_ACTIVITY'),
]);
</script>

<template>
  <div class="w-full">
    <div
      v-if="isLoading"
      role="status"
      :aria-label="t('SUPPORT_CASES.LIST.LOADING')"
      aria-busy="true"
      class="flex flex-col gap-4 py-4"
      data-testid="case-list-skeleton"
    >
      <div
        v-for="row in SKELETON_ROWS"
        :key="row"
        class="grid items-center grid-cols-6 gap-4"
      >
        <Skeleton variant="text" :announce="false" class="col-span-2" />
        <Skeleton variant="text" :announce="false" />
        <Skeleton variant="text" :announce="false" />
        <Skeleton variant="text" :announce="false" />
        <Skeleton variant="text" :announce="false" />
      </div>
    </div>

    <EmptyStateLayout
      v-else-if="hasError"
      compact
      icon="i-lucide-triangle-alert"
      :title="t('SUPPORT_CASES.LIST.ERROR.TITLE')"
      :subtitle="t('SUPPORT_CASES.LIST.ERROR.SUBTITLE')"
      data-testid="case-list-error"
    >
      <template #actions>
        <Button
          size="sm"
          :label="t('SUPPORT_CASES.LIST.ERROR.ACTION')"
          @click="emit('retry')"
        />
      </template>
    </EmptyStateLayout>

    <EmptyStateLayout
      v-else-if="!cases.length && isFiltered"
      compact
      icon="i-lucide-filter"
      :title="t('SUPPORT_CASES.LIST.NO_RESULTS.TITLE')"
      :subtitle="t('SUPPORT_CASES.LIST.NO_RESULTS.SUBTITLE')"
      data-testid="case-list-no-results"
    >
      <template #actions>
        <Button
          size="sm"
          variant="outline"
          color="slate"
          :label="t('SUPPORT_CASES.LIST.NO_RESULTS.ACTION')"
          @click="emit('clearFilters')"
        />
      </template>
    </EmptyStateLayout>

    <EmptyStateLayout
      v-else-if="!cases.length"
      compact
      icon="i-lucide-ticket"
      :title="t('SUPPORT_CASES.LIST.EMPTY.TITLE')"
      :subtitle="t('SUPPORT_CASES.LIST.EMPTY.SUBTITLE')"
      data-testid="case-list-empty"
    />

    <BaseTable
      v-else
      :headers="headers"
      :items="cases"
      data-testid="case-list-table"
    >
      <template #row="{ items }">
        <CaseListRow
          v-for="supportCase in items"
          :key="supportCase.id"
          :support-case="supportCase"
          @select="emit('select', $event)"
        />
      </template>
    </BaseTable>
  </div>
</template>
